import { useEffect, useState } from "react";
import { ScrollText, Download, Search } from "lucide-react";
import DashboardLayout from "../../layouts/DashboardLayout";
import useFeatures from "../../hooks/useFeatures";
import api from "../../api";

// Audit & access log (backend clinical/access/audit_log_routes.py), org admins.
// Who changed what (field-level before/after) and who opened patient records.
const fmt = (iso) => new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });
const val = (v) => (v === null || v === undefined || v === "" ? "empty" : typeof v === "object" ? JSON.stringify(v) : String(v));

export default function AuditLog() {
  const features = useFeatures();
  const [kind, setKind] = useState("all");
  const [patient, setPatient] = useState(null);
  const [pq, setPq] = useState("");
  const [presults, setPresults] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (pq.trim().length < 2) { setPresults([]); return; }
    const t = setTimeout(() => api.fetchPatients({ search: pq.trim(), limit: 8 }).then((r) => setPresults(r.data?.data || [])).catch(() => setPresults([])), 300);
    return () => clearTimeout(t);
  }, [pq]);
  useEffect(() => {
    if (!features.has("audit_log")) return;
    api.getAuditLog({ kind, patient_id: patient?.id, from: from || undefined, to: to || undefined, page })
      .then((r) => { setData(r.data); setError(""); }).catch((e) => setError(e?.response?.data?.error || "Couldn't load the log."));
  }, [features.ready, kind, patient, from, to, page]); // eslint-disable-line
  if (features.ready && !features.has("audit_log")) return <DashboardLayout><div className="p-8 text-secondary-600">The audit log isn't switched on for your organisation yet.</div></DashboardLayout>;

  const exportCsv = async () => {
    const r = await api.exportAccessCsv(patient.id);
    const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `access-log-${patient.name.replace(/\s+/g, "-")}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-secondary-900"><ScrollText className="w-6 h-6 text-primary-600" /> Audit log</h1>
          <p className="text-sm text-secondary-600">Who changed patient records and who opened them. For a patient's data-access request, pick the patient and export their access log.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <div className="flex rounded-lg border border-secondary-300 overflow-hidden">
            {[["all", "Everything"], ["changes", "Changes"], ["access", "Who opened"]].map(([k, v]) => (
              <button key={k} type="button" onClick={() => { setKind(k); setPage(1); }} className={`px-3 py-1.5 ${kind === k ? "bg-secondary-800 text-white" : "bg-white text-secondary-700"}`}>{v}</button>
            ))}
          </div>
          {patient ? (
            <span className="inline-flex items-center gap-2 rounded-lg border border-primary-300 bg-primary-50 px-3 py-1.5">
              {patient.name}<button type="button" onClick={() => { setPatient(null); setPage(1); }} className="text-xs text-primary-700 underline">clear</button>
            </span>
          ) : (
            <div className="relative">
              <Search className="absolute left-2.5 top-2 w-4 h-4 text-secondary-400" />
              <input value={pq} onChange={(e) => setPq(e.target.value)} placeholder="Filter by patient" aria-label="Filter by patient" className="rounded-lg border border-secondary-300 py-1.5 pl-8 pr-3" />
              {presults.length > 0 && (
                <ul className="absolute z-10 mt-1 w-64 rounded-lg border border-secondary-200 bg-white shadow">
                  {presults.map((p) => <li key={p.id}><button type="button" onClick={() => { setPatient(p); setPq(""); setPresults([]); setPage(1); }} className="w-full px-3 py-2 text-left hover:bg-secondary-50">{p.name} <span className="text-xs text-secondary-500">{p.contact}</span></button></li>)}
                </ul>
              )}
            </div>
          )}
          <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} aria-label="From" className="rounded-lg border border-secondary-300 px-2 py-1.5" />
          <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} aria-label="To" className="rounded-lg border border-secondary-300 px-2 py-1.5" />
          {patient && <button type="button" onClick={exportCsv} className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 font-semibold text-white"><Download className="w-4 h-4" /> Export who opened this record</button>}
        </div>
        {error ? <p className="text-sm text-rose-600">{error}</p> : !data ? <p className="text-sm text-secondary-500">Loading…</p> : (
          <>
            <ul className="divide-y divide-secondary-100 rounded-xl border border-secondary-100 bg-white">
              {data.items.length === 0 && <li className="p-6 text-center text-sm text-secondary-500">Nothing in this range.</li>}
              {data.items.map((i, n) => (
                <li key={n} className="px-4 py-3 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${i.type === "access" ? "bg-sky-100 text-sky-800" : "bg-amber-100 text-amber-800"}`}>{i.type === "access" ? "Opened" : "Changed"}</span>
                    <span className="font-semibold text-secondary-900">{i.actor}</span>
                    <span className="text-secondary-700">{i.type === "access" ? i.label : i.action.replace(/_/g, " ")}</span>
                    {i.patient_name && <span className="text-secondary-600">· {i.patient_name}</span>}
                    {i.entity !== "patient" && <span className="text-secondary-500">· {i.entity} #{i.entity_id}</span>}
                    <span className="ml-auto text-xs text-secondary-500">{fmt(i.at)}</span>
                  </div>
                  {i.type === "change" && Object.keys(i.changes || {}).length > 0 && (
                    <ul className="mt-1.5 space-y-0.5 text-xs text-secondary-700">
                      {Object.entries(i.changes).slice(0, 8).map(([f, c]) => (
                        <li key={f}><span className="font-semibold">{f.replace(/_/g, " ")}:</span> <span className="text-secondary-500 line-through">{val(c?.old)}</span> → {val(c?.new)}</li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
            <div className="flex justify-between">
              <button type="button" disabled={page === 1} onClick={() => setPage((p) => p - 1)} className="rounded-lg border border-secondary-300 px-3 py-1.5 text-sm disabled:opacity-40">Newer</button>
              <button type="button" disabled={!data.has_more} onClick={() => setPage((p) => p + 1)} className="rounded-lg border border-secondary-300 px-3 py-1.5 text-sm disabled:opacity-40">Older</button>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
